import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

export type ToastTone = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  title: string;
  description?: string;
  tone: ToastTone;
}

interface ToastContextValue {
  toasts: Toast[];
  show: (toast: Omit<Toast, 'id'>) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Snackbar do Figma: card branco com sombra e um ícone de 20px preenchido.
 * `warning` e `info` usam a variante "Text", que é só o texto, sem ícone.
 */
const TONE_ICON: Record<ToastTone, { color: string; glyph: 'check' | 'x' } | null> = {
  success: { color: 'bg-success-solid text-on-success', glyph: 'check' },
  error: { color: 'bg-danger-solid text-on-danger', glyph: 'x' },
  warning: null,
  info: null,
};

const AUTO_DISMISS_MS = 5000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = crypto.randomUUID();
      setToasts((current) => [...current, { ...toast, id }]);
      setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toasts, show, dismiss }), [toasts, show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed bottom-3 right-3 z-[60] flex flex-col gap-3">
        {toasts.map((toast) => {
          const icon = TONE_ICON[toast.tone];

          return (
            <div
              key={toast.id}
              role="alert"
              onClick={() => dismiss(toast.id)}
              className="flex w-80 max-w-[378px] min-h-14 cursor-pointer items-center gap-2 rounded-xl border-[0.5px] border-line bg-surface p-4 shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-1px_rgba(0,0,0,0.06)] animate-in fade-in slide-in-from-bottom-2 duration-150"
            >
              {icon && (
                <span
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                    icon.color,
                  )}
                  aria-hidden="true"
                >
                  {icon.glyph === 'check' ? (
                    <Check size={12} strokeWidth={3} />
                  ) : (
                    <X size={12} strokeWidth={3} />
                  )}
                </span>
              )}

              <div className="min-w-0 flex-1">
                <p className="text-sm leading-[18px] text-ink">{toast.title}</p>
                {toast.description && (
                  <p className="mt-1 text-xs leading-[15px] text-ink-muted">{toast.description}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast precisa estar dentro de <ToastProvider>.');
  return context;
}
